import { Fragment } from "react";
import { Dialog, Transition, TransitionChild } from "@headlessui/react";
import { Hotkey } from "~/components/Hotkey";
import {
    RecentSearchItem,
    SearchResultGroup,
    SearchResultItem,
} from "../../types";
import { SearchWindow } from "../SearchWindow";
import { useNavigatorPlatform } from "~/modules/app/hooks/useNavigatorPlatform";
import { useFilteredSearchResults } from "../../util/useFilteredSearchResults";
import * as Analytics from "../../analytics";

// // // //

const OPEN_HOTKEY_APPLE = "cmd+k";
const OPEN_HOTKEY_NON_APPLE = "ctrl+k";

// // // //

/**
 * Global Search Command Palette component
 */
export function CommandPalette(props: {
    groups: SearchResultGroup[];
    loadingSearch: boolean;
    recentSearches?: RecentSearchItem[];
    onSelectSearchResult?: (result: SearchResultItem) => void;
    onClearRecentSearches?: () => void;
    onQueryChange?: (query: string) => void;
}) {
    const { isAppleOS } = useNavigatorPlatform();

    const { query, setQuery, open, setOpen, searchGroupings, filteredItems } =
        useFilteredSearchResults(props);

    return (
        <>
            {/* Setup CMD+K hotkey for Apple devices */}
            {/* NOTE - setting the `keyName` prop dynamically introduces some unreliability, */}
            {/* so we have one <Hotkey /> instance for each platform */}
            {isAppleOS && (
                <Hotkey
                    keyName={OPEN_HOTKEY_APPLE}
                    onKeyDown={() => {
                        if (!open) {
                            setOpen(true);
                            Analytics.trackCommandPaletteOpen();
                        }
                    }}
                />
            )}

            {/* Setup CTLR+K hotkey for non-Apple devices */}
            {/* NOTE - setting the `keyName` prop dynamically introduces some unreliability, */}
            {/* so we have one <Hotkey /> instance for each platform */}
            {!isAppleOS && (
                <Hotkey
                    keyName={OPEN_HOTKEY_NON_APPLE}
                    onKeyDown={() => {
                        if (!open) {
                            setOpen(true);
                            Analytics.trackCommandPaletteOpen();
                        }
                    }}
                />
            )}

            <Transition.Root
                show={open}
                as={Fragment}
                afterLeave={() => setQuery("")}
                appear
            >
                <Dialog
                    as="div"
                    className="relative z-300"
                    onClose={() => {
                        setOpen(false);
                        Analytics.trackCommandPaletteClose();
                    }}
                >
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-100"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-slate-500/25 transition-opacity" />
                    </TransitionChild>

                    <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-100"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-100"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="mx-auto max-w-3xl transform overflow-hidden rounded-xl bg-card shadow-2xl ring-1 ring-black/5 transition-all">
                                <SearchWindow
                                    query={query}
                                    autofocus
                                    setQuery={(q) => {
                                        setQuery(q);
                                        props.onQueryChange?.(q);
                                    }}
                                    setOpen={setOpen}
                                    loadingSearch={props.loadingSearch}
                                    recentSearches={props.recentSearches}
                                    onClearRecentSearches={
                                        props.onClearRecentSearches
                                    }
                                    onClose={() => {
                                        Analytics.trackCommandPaletteClose();
                                    }}
                                    items={filteredItems}
                                    groups={searchGroupings}
                                    onSelectResult={(result) => {
                                        props.onSelectSearchResult?.(result);
                                        Analytics.trackCommandPaletteSelectResult(
                                            {
                                                label: result.content.label,
                                                href: result.href,
                                            },
                                        );
                                    }}
                                />
                            </Dialog.Panel>
                        </TransitionChild>
                    </div>
                </Dialog>
            </Transition.Root>
        </>
    );
}
